// ==============================================================================
// VERSION INFORMATION
// ==============================================================================
// __version__ = "1.5.0"
//
// Changelog:
//
// Version 1.5.0:
// - [Changed] The `getAgendaItemsByDate` function now accepts an optional
//   `status` parameter to allow filtering agenda items by 'planned' or
//   'completed'.
//
// Version 1.4.1:
// - [Changed] Modified `updateAgendaItemStatus` to accept a dynamic status
//   payload, allowing items to be toggled between 'planned' and 'completed'.
// ==============================================================================
import apiClient from './api';
import {
    TodoItem, JournalEntry, MsgResponse, Goal, Habit, HabitTracking,
    HabitTrackingUpdatePayload,
    WellBeing, WellBeingUpdatePayload, AgendaItem, AgendaItemCreatePayload, AgendaItemUpdatePayload,
    DailyQuoteResponse,
    TodoItemCreatePayload,
    TodoItemUpdatePayload,
    WeeklySummary,
    WeeklySummaryCreatePayload,
    EngagementHeatmapResponse,
    HabitHeatmapResponse, 
    TimeDistributionResponse,
    DailyBriefing,
    TaskCreationResponse,
    AIGeneratedAgendaItem
} from '../types';
import {
    ItemPriorityEnum, ItemStatusEnum, GoalStatusEnum, HabitFrequencyTypeEnum, HabitStatusEnum, HabitTrackingStatusEnum
} from '../types/enums';
import { AxiosError } from 'axios';

// NEW: Response types for seek endpoints
export interface NextDateResponse {
  next_date: string | null;
}
export interface PreviousDateResponse {
  previous_date: string | null;
}

// NEW: Type definitions for the activity report
export interface ActivityLogEntry {
  timestamp: string;
  activity_type: string; // e.g., 'TODO_CREATED', 'QUOTE_FAVORITED'
  description: string;
}

export interface ActivityReport {
  report_period: {
    start_date: string;
    end_date: string;
  };
  activity_summary: ActivityLogEntry[];
}

export interface AIOrganizeDayRequest {
    client_id: number;
    plan_date: string;
}


// Payloads for creating/updating items
export interface JournalEntryCreatePayload {
  entry_date: string; // YYYY-MM-DD
  title?: string | null;
  content: string;
  category_id?: number | null;
}

export interface JournalEntryUpdatePayload {
  title?: string | null;
  content?: string;
  category_id?: number | null;
  shared_with_coach?: boolean;
}

export interface GoalCreatePayload {
  title: string;
  description?: string | null;
  category_id?: number | null;
  status?: GoalStatusEnum;
  priority?: ItemPriorityEnum | null;
  start_date?: string | null;
  target_date?: string | null;
  plan_date?: string | null;
  goal_template_id?: number | null;
  parent_goal_id?: number | null;
  client_user_id?: number | null;
  progress_percentage?: number; 
}

export interface GoalUpdatePayload {
  title?: string;
  description?: string | null;
  category_id?: number | null;
  status?: GoalStatusEnum;
  priority?: ItemPriorityEnum | null;
  start_date?: string | null;
  target_date?: string | null;
  plan_date?: string | null;
  goal_template_id?: number | null;
  parent_goal_id?: number | null;
  client_user_id?: number | null;
  progress_percentage?: number; 
}

export interface ObjectiveCreatePayload {
  title: string;
  parent_goal_id?: number | null;
  plan_date?: string | null | undefined;
  description?: string | null | undefined;
  status?: ItemStatusEnum;
  priority?: ItemPriorityEnum | null;
  target_completion_date?: string | null | undefined;
}

export interface ObjectiveUpdatePayload {
  title?: string;
  parent_goal_id?: number | null;
  plan_date?: string | null | undefined;
  description?: string | null | undefined;
  status?: ItemStatusEnum;
  priority?: ItemPriorityEnum | null;
  target_completion_date?: string | null | undefined;
}

export interface HabitCreatePayload {
    habit_name: string;
    habit_template_id?: number | null;
    description?: string | null;
    category_id?: number | null;
    start_date: string;
    end_date?: string | null;
    frequency_type: HabitFrequencyTypeEnum;
    target_times_per_week?: number | null;
    specific_week_days?: number[] | null;
    reminder_time?: string | null;
    target_metric?: string | null;
    target_value?: number | null;
    status?: HabitStatusEnum;
    client_user_id?: number | null;
}

export interface HabitUpdatePayload {
    habit_name?: string;
    habit_template_id?: number | null;
    description?: string | null;
    category_id?: number | null;
    start_date?: string;
    end_date?: string | null;
    frequency_type?: HabitFrequencyTypeEnum;
    target_times_per_week?: number | null;
    specific_week_days?: number[] | null;
    reminder_time?: string | null;
    target_metric?: string | null;
    target_value?: number | null;
    status?: HabitStatusEnum;
    client_user_id?: number | null;
}

export interface HabitTrackingCreatePayload {
    tracking_date: string;
    status: HabitTrackingStatusEnum;
    recorded_value?: string;
    quality_rating?: number;
    notes?: string;
}

// --- Todos ---
const getTodos = async (params: { skip?: number; limit?: number; clientId?: number } = {}): Promise<TodoItem[]> => {
  const queryParams: any = {};
  if (params.skip !== undefined) queryParams.skip = params.skip;
  if (params.limit !== undefined) queryParams.limit = params.limit;
  if (params.clientId !== undefined) queryParams.clientId = params.clientId;
  
  const { data } = await apiClient.get<TodoItem[]>('/planner/todos/', { params: queryParams }); 
  return data;
};

const getTodoById = async (todoId: number, clientId?: number): Promise<TodoItem> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.get<TodoItem>(`/planner/todos/${todoId}`, { params });
    return data;
};

const getTodosByDate = async (planDate: string, params?: { categoryId?: number | null; clientId?: number }): Promise<TodoItem[]> => {
  const queryParams: any = {};
  if (params?.categoryId) {
    queryParams.categoryId = params.categoryId;
  }
  if (params?.clientId) {
    queryParams.clientId = params.clientId;
  }
  const { data } = await apiClient.get<TodoItem[]>(`/planner/todos/by_date/${planDate}`, { params: queryParams });
  return data;
};

const createTodo = async (payload: TodoItemCreatePayload): Promise<TodoItem> => {
  const { client_user_id, ...bodyPayload } = payload;
  const params = client_user_id ? { clientId: client_user_id } : {};
  const { data } = await apiClient.post<TodoItem>('/planner/todos/', bodyPayload, { params });
  return data;
};

const updateTodo = async (todoId: number, payload: TodoItemUpdatePayload, clientId?: number): Promise<TodoItem> => {
  const { client_user_id, ...bodyPayload } = payload;
  const params = clientId ? { clientId } : (client_user_id ? { clientId: client_user_id } : {});
  const { data } = await apiClient.put<TodoItem>(`/planner/todos/${todoId}`, bodyPayload, { params });
  return data;
};

const deleteTodo = async (todoId: number, clientId?: number): Promise<void> => {
  const params = clientId ? { clientId } : {};
  await apiClient.delete(`/planner/todos/${todoId}`, { params });
};

// --- Journal ---
const getJournalEntryByDate_Single = async (entryDate: string, clientId?: number): Promise<JournalEntry | null> => {
  try {
    const params: { clientId?: number } = {};
    if (clientId) { params.clientId = clientId; }
    const { data } = await apiClient.get<JournalEntry>(`/planner/journal-entries/by_date/${entryDate}`, { params });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response && axiosError.response.status === 404) {
      return null;
    }
    throw error;
  }
};

const getJournalEntryById = async (entryId: number, clientId?: number): Promise<JournalEntry> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.get<JournalEntry>(`/planner/journal-entries/${entryId}`, { params });
    return data;
};

const getJournalEntriesByDate = async (entryDate: string, params: { skip?: number; limit?: number; categoryId?: number | null; clientId?: number; } = {}): Promise<JournalEntry[]> => {
  const { skip = 0, limit = 100, categoryId, clientId } = params;
  const queryParams: { skip: number; limit: number; categoryId?: number; clientId?: number; } = { skip, limit };
  if (categoryId) { queryParams.categoryId = categoryId; }
  if (clientId) { queryParams.clientId = clientId; }
  try {
    const { data } = await apiClient.get<JournalEntry[]>(`/planner/journal-entries/by_date/${entryDate}`, {
      params: queryParams
    });
    return data;
  } catch (error) {
    console.error(`Failed to fetch journal entries for date ${entryDate}:`, error);
    throw error;
  }
};

const createJournalEntry = async (payload: JournalEntryCreatePayload, clientId?: number): Promise<JournalEntry> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.post<JournalEntry>('/planner/journal-entries/', payload, { params });
    return data;
}

const updateJournalEntryById = async (entryId: number, payload: JournalEntryUpdatePayload, clientId?: number): Promise<JournalEntry> => {
  const params = clientId ? { clientId } : {};
  const { data } = await apiClient.put<JournalEntry>(`/planner/journal-entries/${entryId}`, payload, { params });
  return data;
};

const deleteJournalEntryById = async (entryId: number, clientId?: number): Promise<MsgResponse> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.delete<MsgResponse>(`/planner/journal-entries/${entryId}`, { params });
    return data;
};

// --- Goals ---
const getGoals = async (filters?: { status?: GoalStatusEnum; skip?: number; limit?: number; categoryId?: number | null; clientId?: number; }): Promise<Goal[]> => {
  const { data } = await apiClient.get<Goal[]>('/planner/goals/', { params: filters });
  return data;
};

const getGoalById = async (goalId: number, clientId?: number): Promise<Goal> => {
  const params = clientId ? { clientId } : {};
  const { data } = await apiClient.get<Goal>(`/planner/goals/${goalId}`, { params });
  return data;
};

const createGoal = async (payload: GoalCreatePayload): Promise<Goal> => {
  const { client_user_id, ...bodyPayload } = payload;
  const params = client_user_id ? { clientId: client_user_id } : {};
  const { data } = await apiClient.post<Goal>('/planner/goals/', bodyPayload, { params });
  return data;
};

const updateGoal = async (goalId: number, payload: GoalUpdatePayload, clientId?: number): Promise<Goal> => {
  const { client_user_id, ...bodyPayload } = payload;
  const params = clientId ? { clientId } : (client_user_id ? { clientId: client_user_id } : {});
  const { data } = await apiClient.put<Goal>(`/planner/goals/${goalId}`, bodyPayload, { params });
  return data;
};

const deleteGoal = async (goalId: number, clientId?: number): Promise<void> => {
  const params = clientId ? { clientId } : {};
  await apiClient.delete(`/planner/goals/${goalId}`, { params });
};

// --- Habits ---
const getHabits = async (filters: { status?: HabitStatusEnum; skip?: number; limit?: number; categoryId?: number | null; clientId?: number; } = {}): Promise<Habit[]> => {
  const { data } = await apiClient.get<Habit[]>('/planner/habits/', { params: filters });
  return data;
};

const getHabitById = async (habitId: number, clientId?: number): Promise<Habit> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.get<Habit>(`/planner/habits/${habitId}`, { params });
    return data;
};

const createHabit = async (payload: HabitCreatePayload): Promise<Habit> => {
  const { client_user_id, ...bodyPayload } = payload;
  const params = client_user_id ? { clientId: client_user_id } : {};
  const { data } = await apiClient.post<Habit>('/planner/habits/', bodyPayload, { params });
  return data;
};

const updateHabit = async (habitId: number, payload: HabitUpdatePayload, clientId?: number): Promise<Habit> => {
  const { client_user_id, ...bodyPayload } = payload;
  const params = clientId ? { clientId } : (client_user_id ? { clientId: client_user_id } : {});
  const { data } = await apiClient.put<Habit>(`/planner/habits/${habitId}`, bodyPayload, { params });
  return data;
};

const deleteHabit = async (habitId: number, clientId?: number): Promise<MsgResponse> => {
  const params = clientId ? { clientId } : {};
  const { data } = await apiClient.delete<MsgResponse>(`/planner/habits/${habitId}`, { params });
  return data;
};

const getHabitHeatmap = async (habitId: number, startDate: string, endDate: string, clientId?: number): Promise<HabitHeatmapResponse> => {
  const params: { start_date: string; end_date: string; clientId?: number } = {
    start_date: startDate,
    end_date: endDate,
  };
  if (clientId) {
    params.clientId = clientId;
  }
  const { data } = await apiClient.get<HabitHeatmapResponse>(`/planner/habits/${habitId}/heatmap`, { params });
  return data;
};


// --- Habit Tracking ---
const trackHabit = async (habitId: number, payload: HabitTrackingCreatePayload, clientId?: number): Promise<HabitTracking> => {
  const params = clientId ? { clientId } : {};
  const { data } = await apiClient.post<HabitTracking>(`/planner/habits/${habitId}/track`, payload, { params });
  return data;
};

const getHabitTrackingForDate = async (habitId: number, trackingDate: string, clientId?: number): Promise<HabitTracking | null> => {
  try {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.get<HabitTracking>(`/planner/habits/${habitId}/track/by_date/${trackingDate}`, { params });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response && axiosError.response.status === 404) {
      return null;
    }
    throw error;
  }
};

const getHabitTrackingForAllHabitsByDate = async (trackingDate: string, clientId?: number): Promise<HabitTracking[]> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.get<HabitTracking[]>(`/planner/habits/track/by_date/${trackingDate}/all`, { params });
    return data;
};

const getHabitTrackingForDateRange = async (habitId: number, startDate: string, endDate: string, clientId?: number): Promise<HabitTracking[]> => {
  const params: { start_date: string, end_date: string, clientId?: number } = { start_date: startDate, end_date: endDate };
  if (clientId) params.clientId = clientId;
  const { data } = await apiClient.get<HabitTracking[]>(`/planner/habits/${habitId}/track/range`, { params });
  return data;
};


const updateHabitTracking = async (habitId: number, trackingDate: string, payload: HabitTrackingUpdatePayload, clientId?: number): Promise<HabitTracking> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.put<HabitTracking>(`/planner/habits/${habitId}/track/by_date/${trackingDate}`, payload, { params });
    return data;
};

const deleteHabitTracking = async (habitId: number, trackingDate: string, clientId?: number): Promise<MsgResponse> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.delete<MsgResponse>(`/planner/habits/${habitId}/track/by_date/${trackingDate}`, { params });
    return data;
};

const recalculateStreaks = async (habitId: number, clientId?: number): Promise<Habit> => {
  const params = clientId ? { clientId } : {};
  const { data } = await apiClient.post<Habit>(`/planner/habits/${habitId}/recalculate-streaks`, null, { params });
  return data;
};

const resetCurrentStreak = async (habitId: number, clientId?: number): Promise<Habit> => {
  const params = clientId ? { clientId } : {};
  const { data } = await apiClient.post<Habit>(`/planner/habits/${habitId}/reset-streak`, null, { params });
  return data;
};


// --- Well-Being ---
const getWellBeingByDate = async (entryDate: string, clientId?: number): Promise<WellBeing | null> => {
    try {
        const params: { clientId?: number } = {};
        if (clientId) { params.clientId = clientId; }
        const { data } = await apiClient.get<WellBeing>(`/planner/well-being/by_date/${entryDate}`, { params });
        return data;
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response && axiosError.response.status === 404) {
            return null;
        }
        throw error;
    }
};

const getHistoricalWellBeing = async (startDate: string, endDate: string, clientId?: number): Promise<WellBeing[]> => {
  const params: { clientId?: number; start_date: string; end_date: string; } = {
    start_date: startDate,
    end_date: endDate,
  };
  if (clientId) { params.clientId = clientId; }
  const { data } = await apiClient.get<WellBeing[]>('/planner/well-being/history', { params });
  return data;
};


const upsertWellBeingEntry = async (entryDate: string, payload: WellBeingUpdatePayload, clientId?: number): Promise<WellBeing> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.put<WellBeing>(`/planner/well-being/by_date/${entryDate}`, payload, { params });
    return data;
};

const deleteWellBeingEntry = async (entryDate: string, clientId?: number): Promise<MsgResponse> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.delete<MsgResponse>(`/planner/well-being/by_date/${entryDate}`, { params });
    return data;
};

// --- Agenda Items ---
const createAgendaItem = async (payload: AgendaItemCreatePayload, clientId?: number): Promise<AgendaItem> => {
  const params = clientId ? { clientId } : {};
  const { data } = await apiClient.post<AgendaItem>('/planner/agenda/', payload, { params });
  return data;
};

const getAgendaItemsByDate = async (agendaDateIso: string, params: { clientId?: number; categoryId?: number | null; status?: 'planned' | 'completed' } = {}): Promise<AgendaItem[]> => {
  const { clientId, categoryId, status } = params;
  const queryParams: { clientId?: number; categoryId?: number; status?: 'planned' | 'completed' } = {};
  if (clientId) queryParams.clientId = clientId;
  if (categoryId) queryParams.categoryId = categoryId;
  if (status) queryParams.status = status;
  
  const { data } = await apiClient.get<AgendaItem[]>(`/planner/agenda/by_date/${agendaDateIso}`, { params: queryParams });
  return data;
};

const getAgendaItemById = async (itemId: number, clientId?: number): Promise<AgendaItem> => {
  const params: { clientId?: number } = {};
  if (clientId) {
    params.clientId = clientId;
  }
  const { data } = await apiClient.get<AgendaItem>(`/planner/agenda/${itemId}`, { params });
  return data;
};

const updateAgendaItem = async (itemId: number, payload: AgendaItemUpdatePayload, clientId?: number): Promise<AgendaItem> => {
  const params = clientId ? { clientId } : {};
  const { data } = await apiClient.put<AgendaItem>(`/planner/agenda/${itemId}`, payload, { params });
  return data;
};

const updateAgendaItemStatus = async (itemId: number, status: 'completed' | 'planned', clientId?: number): Promise<AgendaItem> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.patch<AgendaItem>(`/planner/agenda/${itemId}/status`, { status }, { params });
    return data;
};

const deleteAgendaItem = async (itemId: number, clientId?: number): Promise<MsgResponse> => {
  const params = clientId ? { clientId } : {};
  const { data } = await apiClient.delete<MsgResponse>(`/planner/agenda/${itemId}`, { params });
  return data;
};

// --- Time Distribution Report ---
const getTimeDistribution = async (startDate: string, endDate: string, clientId?: number): Promise<TimeDistributionResponse> => {
  const params: { start_date: string, end_date: string, clientId?: number } = { start_date: startDate, end_date: endDate };
  if (clientId) {
    params.clientId = clientId;
  }
  const { data } = await apiClient.get<TimeDistributionResponse>('/planner/agenda/time-distribution', { params });
  return data;
};

// --- Seek Endpoints ---
const getNextAgendaEntryDate = async (fromDate: string, clientId?: number): Promise<NextDateResponse> => {
  const params: { from_date: string; clientId?: number } = { from_date: fromDate };
  if (clientId) {
    params.clientId = clientId;
  }
  const { data } = await apiClient.get<NextDateResponse>('/planner/agenda/next-entry-date', { params });
  return data;
};

const getPreviousAgendaEntryDate = async (fromDate: string, clientId?: number): Promise<PreviousDateResponse> => {
  const params: { from_date: string; clientId?: number } = { from_date: fromDate };
  if (clientId) {
    params.clientId = clientId;
  }
  const { data } = await apiClient.get<PreviousDateResponse>('/planner/agenda/previous-entry-date', { params });
  return data;
};

const getNextJournalEntryDate = async (fromDate: string, clientId?: number): Promise<NextDateResponse> => {
  const params: { from_date: string; clientId?: number } = { from_date: fromDate };
  if (clientId) {
    params.clientId = clientId;
  }
  const { data } = await apiClient.get<NextDateResponse>('/planner/journal-entries/next-entry-date', { params });
  return data;
};

const getPreviousJournalEntryDate = async (fromDate: string, clientId?: number): Promise<PreviousDateResponse> => {
  const params: { from_date: string; clientId?: number } = { from_date: fromDate };
  if (clientId) {
    params.clientId = clientId;
  }
  const { data } = await apiClient.get<PreviousDateResponse>('/planner/journal-entries/previous-entry-date', { params });
  return data;
};

// --- Daily Quote ---
const getDailyQuote = async (clientId?: number): Promise<DailyQuoteResponse | null> => {
  try {
    const params: { clientId?: number } = {};
    if (clientId) { params.clientId = clientId; }
    const response = await apiClient.get<DailyQuoteResponse | null>('/planner/daily-quote/me', { params });
    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response && axiosError.response.status === 404) {
      return null;
    }
    console.error('Error fetching daily quote:', error);
    throw error;
  }
};

// --- Client Activity Reporting ---
const getClientActivityReport = async (clientId: number, startDate: string, endDate: string): Promise<ActivityReport> => {
  const { data } = await apiClient.get<ActivityReport>(`/reports/client-activity/${clientId}`, {
    params: { start_date: startDate, end_date: endDate }
  });
  return data;
};

// --- Weekly Summary ---
const upsertWeeklySummary = async (weekStartDate: string, payload: WeeklySummaryCreatePayload, clientId?: number): Promise<WeeklySummary> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.put<WeeklySummary>(`/planner/weekly-summary/${weekStartDate}`, payload, { params });
    return data;
};

const getWeeklySummaryByDate = async (weekStartDate: string, clientId?: number): Promise<WeeklySummary | null> => {
    try {
        const params = clientId ? { clientId } : {};
        const { data } = await apiClient.get<WeeklySummary>(`/planner/weekly-summary/by-date/${weekStartDate}`, { params });
        return data;
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 404) {
            return null;
        }
        throw error;
    }
};

const getWeeklySummaryById = async (summaryId: number, clientId?: number): Promise<WeeklySummary> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.get<WeeklySummary>(`/planner/weekly-summary/${summaryId}`, { params });
    return data;
};

const getWeeklySummaries = async (clientId?: number): Promise<WeeklySummary[]> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.get<WeeklySummary[]>('/planner/weekly-summary/my-summaries', { params });
    return data;
};

const getLatestWeeklySummary = async (clientId: number): Promise<WeeklySummary | null> => {
    try {
        const { data } = await apiClient.get<WeeklySummary | null>('/planner/weekly-summary/latest', {
            params: { clientId }
        });
        return data;
    } catch (error) {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 404) {
            return null;
        }
        console.error('Failed to get latest weekly summary:', error);
        throw error;
    }
};

const getEngagementHeatmap = async (startDate: string, endDate: string, clientId: number): Promise<EngagementHeatmapResponse> => {
  const { data } = await apiClient.get<EngagementHeatmapResponse>('/planner/heatmap/', {
    params: { start_date: startDate, end_date: endDate, clientId }
  });
  return data;
};

// --- NEW: Daily Briefing ---

const generateDailyBriefingForClient = async (date: string): Promise<TaskCreationResponse> => {
    const { data } = await apiClient.post<TaskCreationResponse>(`/planner/clients/me/daily-briefing/${date}`);
    return data;
};

const getDailyBriefingForClient = async (date: string): Promise<DailyBriefing> => {
    const { data } = await apiClient.get<DailyBriefing>(`/planner/clients/me/daily-briefing/${date}`);
    return data;
};

const generateDailyBriefingForCoach = async (clientId: number, date: string): Promise<TaskCreationResponse> => {
    const { data } = await apiClient.post<TaskCreationResponse>(`/planner/coaches/clients/${clientId}/daily-briefing/${date}`);
    return data;
};

const getDailyBriefingForCoach = async (clientId: number, date: string): Promise<DailyBriefing> => {
    const { data } = await apiClient.get<DailyBriefing>(`/planner/coaches/clients/${clientId}/daily-briefing/${date}`);
    return data;
};

const organizeDayPlan = async (payload: AIOrganizeDayRequest): Promise<TaskCreationResponse> => {
    const { data } = await apiClient.post<TaskCreationResponse>('/planner/organize-day-plan', payload);
    return data;
};

const getAIGeneratedAgenda = async (planDate: string, clientId?: number): Promise<AIGeneratedAgendaItem[]> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.get<AIGeneratedAgendaItem[]>(`/planner/ai-planning/by_date/${planDate}`, { params });
    return data;
};

const updateAIGeneratedAgendaItemStatus = async (itemId: number, status: 'accepted' | 'rejected', clientId?: number): Promise<AIGeneratedAgendaItem> => {
    const params = clientId ? { clientId } : {};
    const { data } = await apiClient.patch<AIGeneratedAgendaItem>(`/planner/ai-planning/${itemId}/status`, { status }, { params });
    return data;
};

export default {  
  getTodosByDate,
  createTodo,
  updateTodo,
  deleteTodo,
  getJournalEntryByDate_Single, 
  getJournalEntriesByDate,
  createJournalEntry,
  updateJournalEntryById,
  deleteJournalEntryById,
  getGoals,
  getGoalById,
  createGoal,
  updateGoal,
  deleteGoal,
  getHabits,
  createHabit,
  updateHabit,
  deleteHabit,
  getHabitHeatmap,
  trackHabit,
  getHabitTrackingForDate,
  getHabitTrackingForAllHabitsByDate,
  updateHabitTracking,
  getWellBeingByDate,
  getHistoricalWellBeing,
  upsertWellBeingEntry,
  deleteWellBeingEntry,
  createAgendaItem,
  getAgendaItemsByDate,
  getAgendaItemById,
  updateAgendaItem,
  updateAgendaItemStatus,
  deleteAgendaItem,
  getDailyQuote,
  getTodos,
  getTodoById,
  getHabitById,
  getClientActivityReport, 
  getNextAgendaEntryDate,
  getPreviousAgendaEntryDate,
  getNextJournalEntryDate,
  getPreviousJournalEntryDate,
  upsertWeeklySummary,
  getLatestWeeklySummary,
  getWeeklySummaryByDate,  
  getWeeklySummaries,
  getWeeklySummaryById,
  getHabitTrackingForDateRange,
  deleteHabitTracking,
  recalculateStreaks,
  resetCurrentStreak,
  getEngagementHeatmap,
  getTimeDistribution,
  getJournalEntryById,
  generateDailyBriefingForClient,
  getDailyBriefingForClient,
  generateDailyBriefingForCoach,
  getDailyBriefingForCoach,
  organizeDayPlan,
  getAIGeneratedAgenda,
  updateAIGeneratedAgendaItemStatus,
};